/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global Office */

import { authService, createRibbonAuthManager, createDialogOpener } from "@carousel-office/shared";
import { ResyncService } from "../services/resyncService";
import { calculateSyncSummary } from "../types/resync";
import { 
    parseDialogMessage, 
    sendToDialog,
    type DialogToParentMessage,
} from "../types/resyncDialogMessages";
import {
    parseAdminDialogMessage,
    sendAdminToDialog,
    type InMemoryConversationData,
} from "@carousel-office/shared";
import { backendClient } from "../services/communication/backendClient";
import { getOrCreatedocumentId } from "../services/documentIdManager";
import { LiveTrackingStorage } from "../services/tracking/liveTrackingStorage";
import { getConversationPersistence } from "../main/globals/persistence";
import { settingsService } from "../services/settingsService";
import GlobalStateManager from "../main/globals/global-state/globalStateManager";
import {
  initializeTaskpaneChatService,
  isTaskpaneChatServiceInitialized,
  createTaskpaneChatConversation,
  sendTaskpaneChatMessage,
} from "../taskpane/services/taskpaneChatService";
import { getSessionLogger } from "../main/services/logging";
import { getLoggingConfig } from "../main/services/logging/config";
import type { AgentCallbacks, ThreadId } from "../main/core/types";
import { UserInterviewTool } from "../main/agents/tools/userInterview";
import { ReorderOrDeleteSlidesTool } from "../main/agents/orchestrator/tools/reorderOrDeleteSlides";
import { LivePowerPointOrchestrator } from "../main/agents/orchestrator/orchestrator";
import { AgentBaseClass } from "../main/agents/base";
import type { ChatMessage, ToolChoice } from "../main/globals/llm/baseAdapter";
import type { OrchestratorRequestData, OrchestratorResponseData } from "../main/services/logging/types";
import { getShapeMetadata } from "../services/shapeMetadataService";
import type { MetadataEntry } from "../services/metadataTypes";
import {
  parseCitationsDialogMessage,
  sendCitationsToDialog,
  type CitationDialogEntry,
  type CitationDialogShapeContext,
} from "../types/citationsDialogMessages";

const LOG_SERVER_URL = getLoggingConfig().serverUrl;

interface SelectedShapeContext {
  slideIndex: number; // 0-indexed
  shapeId: string;
  shapeType?: string;
  shapeName?: string;
}

function normalizeCitationMatchText(text: string): string {
  return text.toLowerCase().replace(/\s+/g, " ").trim();
}

function containsEitherDirection(a: string, b: string): boolean {
  return a.includes(b) || b.includes(a);
}

function filterCitationEntriesBySelection(
  entries: MetadataEntry[],
  selectedText: string
): MetadataEntry[] {
  const selectedNormalized = normalizeCitationMatchText(selectedText);
  const citationEntries = entries.filter((entry) => entry?.type?.toLowerCase() === "citation");

  if (!selectedNormalized) {
    return citationEntries;
  }

  const filtered = citationEntries.filter((entry) => {
    const mapsToTargets = Array.isArray(entry.mapsTo) ? entry.mapsTo : [];
    if (mapsToTargets.length === 0) {
      return true;
    }

    return mapsToTargets.some((target) => {
      const targetTexts = Array.isArray(target?.text)
        ? target.text
            .map((value) => normalizeCitationMatchText(value))
            .filter((value) => value.length > 0)
        : [];

      // If a target only specifies row/col, include it as a shape/cell-level match.
      if (targetTexts.length === 0) {
        return true;
      }

      return targetTexts.some((targetText) => containsEitherDirection(selectedNormalized, targetText));
    });
  });

  // Stable dedupe by sourceContextId.
  const deduped = new Map<string, MetadataEntry>();
  for (const entry of filtered) {
    deduped.set(entry.sourceContextId, entry);
  }
  return Array.from(deduped.values());
}

function getSelectedTextFromDocument(): Promise<string> {
  return new Promise((resolve) => {
    Office.context.document.getSelectedDataAsync(
      Office.CoercionType.Text,
      (result: Office.AsyncResult<string>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value || "");
        } else {
          console.warn("[Commands] Could not read selected text:", result.error);
          resolve("");
        }
      }
    );
  });
}

async function getSelectedShapeContext(): Promise<SelectedShapeContext | null> {
  try {
    return await PowerPoint.run(async (context) => {
      const selectedShapes = context.presentation.getSelectedShapes();
      selectedShapes.load("items");
      const selectedSlides = context.presentation.getSelectedSlides();
      selectedSlides.load("items");
      await context.sync();

      if (selectedShapes.items.length === 0 || selectedSlides.items.length === 0) {
        return null;
      }

      const selectedShape = selectedShapes.items[0];
      selectedShape.load(["id", "type", "name"]);
      const selectedSlide = selectedSlides.getItemAt(0);
      selectedSlide.load("id");
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();

      for (const slide of slides.items) {
        slide.load("id");
      }
      await context.sync();

      let slideIndex = 0;
      for (let i = 0; i < slides.items.length; i++) {
        if (slides.items[i].id === selectedSlide.id) {
          slideIndex = i;
          break;
        }
      }

      return {
        slideIndex,
        shapeId: String(selectedShape.id),
        shapeType: String(selectedShape.type ?? ""),
        shapeName: selectedShape.name ? String(selectedShape.name) : undefined,
      };
    });
  } catch (error) {
    console.warn("[Commands] Could not resolve selected shape context:", error);
    return null;
  }
}

function goToSlideInHost(slideNumber: number): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.goToByIdAsync(slideNumber, Office.GoToType.Index, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) reject(asyncResult.error);
      else resolve();
    });
  });
}

async function captureSlideImageBase64InHost(slideNumber: number): Promise<string> {
  await goToSlideInHost(slideNumber);
  return await PowerPoint.run(async (context) => {
    const slide = context.presentation.slides.getItemAt(slideNumber - 1);
    const imgResult = slide.getImageAsBase64();
    await context.sync();
    return imgResult.value || "";
  });
}

async function runAgentTestInHost(
  test: { id: string; name: string; slideNumber: number; prompt: string },
  runMeta: { runGroupId?: string; runIndex?: number; runName?: string } | undefined,
  progress?: (stage: 'starting' | 'navigating' | 'capturing_before' | 'sending_prompt' | 'capturing_after' | 'done' | 'error', message?: string) => void
): Promise<{ success: boolean; error?: string; responseText?: string; preImagePath?: string; postImagePath?: string; sessionId?: string }> {
  progress?.("starting", `Starting "${test.name}"…`);

  if (!isTaskpaneChatServiceInitialized()) {
    await initializeTaskpaneChatService();
  }

  const logger = getSessionLogger();
  // Use a stable prefix so Admin UI can reconstruct history from sessionId alone.
  // Format: ppt_test_{runGroupId}_{runIndex}_{timestamp}_{rand}
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const rnd = Math.random().toString(36).slice(2, 8);
  const safeRunGroupId = String(runMeta?.runGroupId ?? "solo").replace(/[^a-zA-Z0-9-]/g, "").slice(0, 32) || "solo";
  const safeRunIndex = Number.isFinite(runMeta?.runIndex) ? Math.max(0, Math.floor(runMeta!.runIndex!)) : 0;
  const runIndexToken = String(safeRunIndex).padStart(3, "0");
  const testSessionId = `ppt_test_${safeRunGroupId}_${runIndexToken}_${ts}_${rnd}`;

  logger.startSession(testSessionId); // per-test session for cleanliness
  logger.setMetadata({
    source: "admin_agent_tests",
    testId: test.id,
    testName: test.name,
    testSlideNumber: test.slideNumber,
    runGroupId: safeRunGroupId,
    runIndex: safeRunIndex,
    runName: runMeta?.runName,
  });

  const sessionId = logger.getSessionId() ?? testSessionId;
  const toolName = "agent_test";
  const toolId = `${toolName}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  try {
    logger.logToolStart(
      "test_runner",
      toolName,
      {
        testId: test.id,
        testName: test.name,
        slideNumber: test.slideNumber,
        prompt: test.prompt,
      },
      toolId,
      false
    );

    progress?.("navigating", `Navigating to slide ${test.slideNumber}…`);
    await goToSlideInHost(test.slideNumber);

    progress?.("capturing_before", "Capturing before…");
    const preB64 = await captureSlideImageBase64InHost(test.slideNumber);
    const preImagePath = await logger.logSlideImage(
      test.slideNumber,
      "before",
      `Test ${test.id}: before`,
      preB64,
      toolName
    );

    progress?.("sending_prompt", "Sending prompt…");
    const conversationId = `agent_test_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    await createTaskpaneChatConversation(conversationId);

    const callbacks: AgentCallbacks = {
      onToolStart: (toolName) => {
        // If the agent asks for interactive input, auto-cancel so tests don't hang.
        if (toolName === "userInterview") {
          progress?.("sending_prompt", "Tool: userInterview (auto-cancelled for test run)");
          UserInterviewTool.cancelInterview(
            "Automated test run: no user input available. Proceed with best guess."
          );
        } else if (toolName === "reorderOrDeleteSlides") {
          progress?.("sending_prompt", "Tool: reorderOrDeleteSlides (auto-cancelled for test run)");
          ReorderOrDeleteSlidesTool.cancelApproval("Automated test run: no approvals available.");
        } else {
          progress?.("sending_prompt", `Tool: ${toolName}`);
        }
      },
    };

    const response = await sendTaskpaneChatMessage(test.prompt, conversationId, callbacks);
    const responseText = response?.content ?? "";

    progress?.("capturing_after", "Capturing after…");
    const postB64 = await captureSlideImageBase64InHost(test.slideNumber);
    const postImagePath = await logger.logSlideImage(
      test.slideNumber,
      "after",
      `Test ${test.id}: after`,
      postB64,
      toolName
    );

    logger.logToolComplete("test_runner", toolName, { testId: test.id, slideNumber: test.slideNumber }, toolId);
    progress?.("done", "Done.");

    return { success: true, responseText, preImagePath, postImagePath, sessionId: sessionId ?? logger.getSessionId() ?? undefined };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    progress?.("error", msg);
    try {
      logger.logToolError("test_runner", toolName, msg, toolId);
    } catch {
      // ignore
    }
    return { success: false, error: msg, sessionId: sessionId ?? logger.getSessionId() ?? undefined };
  } finally {
    try {
      await logger.endSession();
    } catch (e) {
      console.warn("[Commands] Failed to end/persist test session:", e);
    }
  }
}

interface SessionLog {
  sessionId: string;
  turns: Array<{ turnNumber?: number; type: string; data: any }>;
}

interface LoggedRequestPayload {
  systemPrompt?: string;
  messages?: ChatMessage[];
  toolChoice?: ToolChoice;
  maxTokens?: number;
  temperature?: number;
  extraBody?: Record<string, unknown>;
  extraHeaders?: Record<string, string>;
  thinking?: { type: "enabled"; budget_tokens: number };
}

interface ReplaySourceData {
  agentName?: string;
  model?: string;
  requestPayload?: LoggedRequestPayload;
  responseText?: string;
}

interface ReplayableAgent {
  replayFromLog: (options: {
    conversationId?: string;
    documentId?: string;
    threadId?: ThreadId;
    systemPrompt: string;
    messages: ChatMessage[];
    modelOverride?: string;
    toolChoice?: ToolChoice;
    maxIterations?: number;
    maxTokens?: number;
    temperature?: number;
    extraBody?: Record<string, unknown>;
    extraHeaders?: Record<string, string>;
    thinking?: { type: "enabled"; budget_tokens: number };
    stream?: boolean;
    callbacks?: AgentCallbacks;
  }) => Promise<{
    content: string;
    messages: ChatMessage[];
    tokensUsed?: number;
    completed: boolean;
    error?: string;
  }>;
}

const CHAT_SESSION_TTL_MS = 30 * 60 * 1000;
const MAX_CHAT_SESSIONS = 20;

const chatSessions = new Map<string, {
  agent: AgentBaseClass;
  systemPrompt: string;
  messages: ChatMessage[];
  conversationId: string;
  documentId: string;
  threadId: ThreadId;
  modelOverride?: string;
  lastUsedAt: number;
}>();

function cleanupChatSessions() {
  const now = Date.now();
  for (const [chatId, session] of chatSessions.entries()) {
    if (now - session.lastUsedAt > CHAT_SESSION_TTL_MS) {
      chatSessions.delete(chatId);
    }
  }
  if (chatSessions.size > MAX_CHAT_SESSIONS) {
    const sorted = Array.from(chatSessions.entries()).sort((a, b) => a[1].lastUsedAt - b[1].lastUsedAt);
    const overflow = sorted.length - MAX_CHAT_SESSIONS;
    for (let i = 0; i < overflow; i += 1) {
      chatSessions.delete(sorted[i][0]);
    }
  }
}

function sanitizeReplayMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((message) => {
    if (!Array.isArray(message.content)) return message;
    const filtered = message.content.filter((part) => {
      if (part.type !== "image_url") return true;
      const url = part.image_url?.url;
      if (!url) return false;
      if (url.startsWith("<")) return false;
      return true;
    });
    return { ...message, content: filtered.length > 0 ? filtered : "" };
  });
}

function buildMessagePreview(content: ChatMessage["content"], maxLength = 240): string {
  if (typeof content === "string") {
    if (content.length <= maxLength) return content;
    return `${content.slice(0, maxLength)}…`;
  }
  if (Array.isArray(content)) {
    return `[${content.length} content parts]`;
  }
  return "[non-text content]";
}

function emitDebugChunks(
  progress: ((stage: 'starting' | 'fetching' | 'running' | 'done' | 'error', message?: string, details?: Record<string, unknown>) => void) | undefined,
  label: string,
  payload: unknown,
  stage: 'starting' | 'fetching' | 'running' | 'done' | 'error' = 'fetching',
  chunkSize = 8000
): void {
  if (!progress) return;
  const json = JSON.stringify(payload, null, 2);
  const total = Math.max(1, Math.ceil(json.length / chunkSize));
  for (let i = 0; i < total; i += 1) {
    const start = i * chunkSize;
    const end = start + chunkSize;
    progress(stage, `${label} (${i + 1}/${total})`, {
      debugLabel: label,
      chunkIndex: i + 1,
      chunkTotal: total,
      chunk: json.slice(start, end),
      totalLength: json.length,
    });
  }
}

async function fetchSessionLogFromServer(sessionId: string): Promise<SessionLog | null> {
  try {
    const response = await fetch(`${LOG_SERVER_URL}/api/get_session_log/${sessionId}`);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.data as SessionLog;
  } catch {
    return null;
  }
}

function extractReplaySource(sessionLog: SessionLog, turnNumber: number): ReplaySourceData | null {
  const requestTurn = sessionLog.turns.find(
    (turn) => turn.type === "orchestrator_request" && turn.turnNumber === turnNumber
  );
  if (!requestTurn) return null;
  const responseTurn = sessionLog.turns.find(
    (turn) =>
      turn.type === "orchestrator_response" &&
      typeof turn.turnNumber === "number" &&
      turn.turnNumber > turnNumber
  );
  const requestData = requestTurn.data as OrchestratorRequestData & { requestPayload?: LoggedRequestPayload };
  const responseData = responseTurn?.data as OrchestratorResponseData | undefined;

  return {
    agentName: requestData.agentName,
    model: requestData.model,
    requestPayload: requestData.requestPayload,
    responseText: responseData?.content,
  };
}

function resolveReplayAgent(
  agentName: string,
  orchestrator: LivePowerPointOrchestrator
): AgentBaseClass | undefined {
  if (agentName === orchestrator.name) return orchestrator as unknown as AgentBaseClass;
  const toolMatch = orchestrator
    .getTools()
    .find(tool => tool instanceof AgentBaseClass && tool.name === agentName) as AgentBaseClass | undefined;
  return toolMatch;
}

async function replayLlmCallInHost(
  params: {
    requestId: string;
    sessionId: string;
    turnNumber: number;
    mode: 'isolated' | 'from_turn';
    modelOverride?: string;
    systemPromptOverride?: string;
    messagesOverride?: ChatMessage[];
  },
  progress?: (stage: 'starting' | 'fetching' | 'running' | 'done' | 'error', message?: string) => void
): Promise<{ success: boolean; responseText?: string; error?: string; sessionId?: string }> {
  progress?.('starting', 'Starting replay...');

  const sessionLog = await fetchSessionLogFromServer(params.sessionId);
  if (!sessionLog) {
    progress?.('error', 'Failed to load session log from server');
    return { success: false, error: "Failed to load session log." };
  }

  progress?.('fetching', 'Extracting request payload...');
  const source = extractReplaySource(sessionLog, params.turnNumber);
  if (!source?.requestPayload) {
    progress?.('error', 'No request payload found');
    return { success: false, error: "Missing request payload in session log." };
  }

  const requestPayload = source.requestPayload;
  const systemPrompt = params.systemPromptOverride ?? requestPayload.systemPrompt;
  const messages = params.messagesOverride ?? requestPayload.messages;

  if (!systemPrompt || !messages || messages.length === 0) {
    progress?.('error', 'Request payload missing system prompt or messages');
    return { success: false, error: "Request payload missing systemPrompt/messages." };
  }

  if (!isTaskpaneChatServiceInitialized()) {
    await initializeTaskpaneChatService();
  }

  const logger = getSessionLogger();
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const rnd = Math.random().toString(36).slice(2, 8);
  const replaySessionId = `ppt_replay_${ts}_${rnd}`;
  logger.startSession(replaySessionId);
  logger.setMetadata({
    source: "admin_replay",
    replayAgentName: source.agentName,
    replayMode: params.mode,
    sourceSessionId: params.sessionId,
    sourceTurnNumber: params.turnNumber,
  });

  const documentId = await getOrCreatedocumentId().catch(() => "unknown_presentation");
  if (documentId) {
    logger.setPresentationName(documentId);
  }

  const conversationId = `admin_replay_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const globalState = GlobalStateManager.getInstance();
  const conversation = globalState.createConversation(conversationId, documentId || "unknown_presentation", "other");
  const orchestrator = new LivePowerPointOrchestrator();
  conversation.agentRegistry.registerAgent("livePowerPointOrchestrator", orchestrator);

  const targetAgent = resolveReplayAgent(source.agentName || orchestrator.name, orchestrator) || orchestrator;
  const resolvedModelOverride = params.modelOverride === "__taskpane__"
    ? undefined
    : params.modelOverride || source.model;

  try {
    progress?.('running', 'Running replay...');
    const result = await (targetAgent as ReplayableAgent).replayFromLog({
      conversationId,
      documentId,
      systemPrompt,
      messages: sanitizeReplayMessages(messages),
      toolChoice: params.mode === "isolated" ? "none" : (requestPayload.toolChoice ?? "auto"),
      maxTokens: requestPayload.maxTokens,
      temperature: requestPayload.temperature,
      extraBody: requestPayload.extraBody,
      extraHeaders: requestPayload.extraHeaders,
      thinking: requestPayload.thinking,
      maxIterations: params.mode === "isolated" ? 1 : undefined,
      stream: false,
      modelOverride: resolvedModelOverride,
    });

    progress?.('done', 'Replay complete');
    return {
      success: result.completed,
      responseText: result.content,
      sessionId: logger.getSessionId() ?? replaySessionId,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    progress?.('error', msg);
    return { success: false, error: msg, sessionId: logger.getSessionId() ?? replaySessionId };
  } finally {
    try {
      await logger.endSession();
    } catch (e) {
      console.warn("[Commands] Failed to end replay session:", e);
    }
  }
}

async function chatSendInHost(
  params: {
    chatId: string;
    sessionId: string;
    turnNumber: number;
    message: string;
    modelOverride?: string;
    stream?: boolean;
  },
  progress?: (stage: 'starting' | 'fetching' | 'running' | 'done' | 'error', message?: string, details?: Record<string, unknown>) => void,
  onChunk?: (chunk: string) => void
): Promise<{ success: boolean; responseText?: string; error?: string; sessionId?: string }> {
  console.log('[chatSendInHost] 🚀 Function called with params:', {
    chatId: params.chatId,
    sessionId: params.sessionId,
    turnNumber: params.turnNumber,
    messageLength: params.message?.length,
    modelOverride: params.modelOverride,
    stream: params.stream,
  });
  progress?.('starting', 'Starting chat...', {
    chatId: params.chatId,
    sessionId: params.sessionId,
    turnNumber: params.turnNumber,
    stream: params.stream ?? true,
    hasModelOverride: !!params.modelOverride,
    messageLength: params.message.length,
    messagePreview: params.message,
  });

  if (!isTaskpaneChatServiceInitialized()) {
    await initializeTaskpaneChatService();
  }

  let chatSession = chatSessions.get(params.chatId);
  cleanupChatSessions();

  if (!chatSession) {
    const sessionLog = await fetchSessionLogFromServer(params.sessionId);
    if (!sessionLog) {
      progress?.('error', 'Failed to load session log', {
        chatId: params.chatId,
        sessionId: params.sessionId,
      });
      return { success: false, error: "Failed to load session log." };
    }

    progress?.('fetching', 'Preparing chat context...');
    const source = extractReplaySource(sessionLog, params.turnNumber);
    if (!source?.requestPayload) {
      progress?.('error', 'Missing request payload', {
        chatId: params.chatId,
        turnNumber: params.turnNumber,
      });
      return { success: false, error: "Missing request payload in session log." };
    }

    const systemPrompt = source.requestPayload.systemPrompt;
    const baseMessages = source.requestPayload.messages;
    if (!systemPrompt || !baseMessages) {
      progress?.('error', 'Request payload missing systemPrompt/messages', {
        chatId: params.chatId,
        systemPromptLength: systemPrompt?.length ?? 0,
        baseMessagesCount: baseMessages?.length ?? 0,
      });
      return { success: false, error: "Request payload missing systemPrompt/messages." };
    }

    const seededMessages: ChatMessage[] = [
      ...sanitizeReplayMessages(baseMessages),
    ];
    if (source.responseText) {
      seededMessages.push({ role: "assistant", content: source.responseText });
    }

    const documentId = await getOrCreatedocumentId().catch(() => "unknown_presentation");
    const conversationId = `admin_chat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const threadId = `chat_thread_${Date.now()}` as ThreadId;
    const globalState = GlobalStateManager.getInstance();
    const conversation = globalState.createConversation(conversationId, documentId || "unknown_presentation", "other");
    const orchestrator = new LivePowerPointOrchestrator();
    conversation.agentRegistry.registerAgent("livePowerPointOrchestrator", orchestrator);
    const agent =
      resolveReplayAgent(source.agentName || orchestrator.name, orchestrator) ||
      (orchestrator as unknown as AgentBaseClass);

    chatSession = {
      agent,
      systemPrompt,
      messages: seededMessages,
      conversationId,
      documentId: documentId || "unknown_presentation",
      threadId,
      modelOverride: params.modelOverride === "__taskpane__" ? undefined : params.modelOverride || source.model,
      lastUsedAt: Date.now(),
    };
    chatSessions.set(params.chatId, chatSession);

    progress?.('fetching', 'Chat context ready', {
      chatId: params.chatId,
      agentName: agent.name,
      model: source.model,
      modelOverride: chatSession.modelOverride ?? null,
      systemPromptLength: systemPrompt.length,
      baseMessagesCount: baseMessages.length,
      seededMessagesCount: seededMessages.length,
      hasResponseText: !!source.responseText,
      conversationId,
      documentId: documentId || "unknown_presentation",
      threadId,
    });

    emitDebugChunks(progress, "requestPayload", source.requestPayload, "fetching");
    emitDebugChunks(progress, "seededMessages", seededMessages, "fetching");
    emitDebugChunks(progress, "systemPrompt", systemPrompt, "fetching");
  }

  if (params.modelOverride !== undefined) {
    chatSession.modelOverride = params.modelOverride === "__taskpane__"
      ? undefined
      : params.modelOverride;
  }
  chatSession.lastUsedAt = Date.now();

  const logger = getSessionLogger();
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const rnd = Math.random().toString(36).slice(2, 8);
  const chatSessionId = `ppt_chat_${ts}_${rnd}`;
  logger.startSession(chatSessionId);
  logger.setMetadata({
    source: "admin_chat",
    chatId: params.chatId,
    sourceSessionId: params.sessionId,
    sourceTurnNumber: params.turnNumber,
  });

  try {
    const userMessage: ChatMessage = { role: "user", content: params.message };
    const updatedMessages = [...chatSession.messages, userMessage];
    const userMessagePreview = buildMessagePreview(userMessage.content, 240);
    progress?.('running', 'Sending message...', {
      chatId: params.chatId,
      agentName: chatSession.agent.name,
      modelOverride: chatSession.modelOverride ?? null,
      systemPromptLength: chatSession.systemPrompt.length,
      updatedMessagesCount: updatedMessages.length,
      userMessageLength: params.message.length,
      userMessagePreview,
    });

    emitDebugChunks(progress, "updatedMessages", updatedMessages, "running");

    const result = await (chatSession.agent as ReplayableAgent).replayFromLog({
      conversationId: chatSession.conversationId,
      documentId: chatSession.documentId,
      threadId: chatSession.threadId,
      systemPrompt: chatSession.systemPrompt,
      messages: updatedMessages,
      toolChoice: "auto",
      maxIterations: 10,
      stream: params.stream ?? true,
      callbacks: onChunk
        ? {
            onChunk: (chunk: string) => {
              onChunk(chunk);
            },
          }
        : undefined,
      modelOverride: chatSession.modelOverride,
    });

    chatSession.messages = result.messages;
    chatSession.lastUsedAt = Date.now();
    progress?.('done', 'Response received', {
      chatId: params.chatId,
      completed: result.completed,
      responseLength: result.content?.length ?? 0,
      responsePreview: buildMessagePreview(result.content || "", 240),
    });
    emitDebugChunks(progress, "result", result, "done");

    return {
      success: result.completed,
      responseText: result.content,
      sessionId: logger.getSessionId() ?? chatSessionId,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    progress?.('error', msg, {
      chatId: params.chatId,
      messageLength: params.message.length,
    });
    return { success: false, error: msg, sessionId: logger.getSessionId() ?? chatSessionId };
  } finally {
    try {
      await logger.endSession();
    } catch (e) {
      console.warn("[Commands] Failed to end chat session:", e);
    }
    chatSession.lastUsedAt = Date.now();
    cleanupChatSessions();
  }
}

async function extractTestsFromSlideNotesInHost(): Promise<{ success: boolean; tests?: Array<{ id: string; name: string; slideNumber: number; prompt: string }>; error?: string }> {
  try {
    console.log('[Commands] Extracting tests from slide notes...');
    
    const tests: Array<{ id: string; name: string; slideNumber: number; prompt: string }> = [];
    
    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items');
      await context.sync();
      
      // Process each slide individually
      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        const slideNumber = i + 1;
        
        // Export this single slide as PPTX
        const pptxResult = slide.exportAsBase64();
        await context.sync();
        const pptxBase64 = pptxResult.value || '';
        
        if (!pptxBase64) {
          continue;
        }
        
        // Parse this slide to get notes via V2 endpoint
        try {
          let notes: string | undefined;
          
          const response = await fetch(`${backendClient.baseUrl}/slides/parse_v2`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': authService.getAuthHeader(),
            },
            body: JSON.stringify({
              pptx_base64: pptxBase64,
              detail_level: 'layout',
              include_notes: true,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            notes = data.slide?.notes;
          }
          
          // Add test if notes found
          if (notes && typeof notes === 'string' && notes.trim()) {
            const timestamp = Date.now().toString(36).slice(-4);
            tests.push({
              id: `slide_${slideNumber}_notes_${timestamp}`,
              name: `Slide ${slideNumber} (from notes)`,
              slideNumber: slideNumber,
              prompt: notes.trim(),
            });
          }
        } catch (error) {
          // Silently skip slides that fail to parse
        }
      }
    });

    console.log(`[Commands] Extracted ${tests.length} tests from slide notes`);
    return { success: true, tests };

  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error('[Commands] Error extracting tests from notes:', msg);
    return { success: false, error: msg };
  }
}

/**
 * Cleanup old conversations based on retention settings
 * Called on startup and after successful login
 * 
 * Uses the ConversationPersistence service for consistent behavior
 * with the rest of the persistence system.
 */
async function cleanupOldConversations(): Promise<void> {
  try {
    const retentionDays = settingsService.getConversationRetentionDays();
    console.log(`[Commands] Cleaning up conversations older than ${retentionDays} days...`);
    
    const persistence = getConversationPersistence();
    const deleted = await persistence.cleanupOldConversations(retentionDays);
    
    if (deleted > 0) {
      console.log(`[Commands] Cleaned up ${deleted} old conversations`);
    } else {
      console.log('[Commands] No old conversations to clean up');
    }
  } catch (error) {
    // Don't fail if cleanup fails - backend may not be running yet
    console.warn('[Commands] Could not cleanup old conversations:', error);
  }
}

const PPT_RIBBON_CONTROLS = {
  tabId: "TabCarousel",
  commandsGroupId: "CommandsGroup",
  personalizationGroupId: "PersonalizationGroup",
  commands: [
    "TaskpaneButton",
    "PlanningButton",
    "AdminButton",
    "SettingsButton",
    "ResyncButton",
    "ManageDataButton",
  ],
  personalization: ["CustomInstructionsButton", "PromptLibraryButton"],
} as const;

const PPT_RIBBON_TARGETS = [
  {
    tabId: PPT_RIBBON_CONTROLS.tabId,
    groups: [
      {
        id: PPT_RIBBON_CONTROLS.commandsGroupId,
        controls: PPT_RIBBON_CONTROLS.commands.map((id) => ({ id })),
      },
      {
        id: PPT_RIBBON_CONTROLS.personalizationGroupId,
        controls: PPT_RIBBON_CONTROLS.personalization.map((id) => ({ id })),
      },
    ],
  },
];

const pptRibbonAuthManager = createRibbonAuthManager({
  authService,
  targets: PPT_RIBBON_TARGETS,
  startupBehavior: {
    authenticated: () => (Office as any)?.StartupBehavior?.load,
    unauthenticated: () => (Office as any)?.StartupBehavior?.none,
  },
  loggerPrefix: "[Commands]",
});

function updateRibbonForAuthState(isAuthenticated: boolean): void {
  pptRibbonAuthManager.updateRibbonForAuthState(isAuthenticated);
}

function scheduleRibbonStateRetry(): void {
  pptRibbonAuthManager.scheduleRibbonStateRetry();
}

function setTaskpaneStartupBehavior(behavior: unknown, reason: string): void {
  pptRibbonAuthManager.setTaskpaneStartupBehavior(behavior, reason);
}

async function syncRibbonFromAuth(): Promise<void> {
  try {
    await pptRibbonAuthManager.syncRibbonFromAuth();
    const isAuthenticated = authService.isAuthenticated();
    console.log("[Commands] AuthService initialized:", { isAuthenticated });

    if (isAuthenticated) {
      await cleanupOldConversations();
    }
  } catch (error) {
    console.error("[Commands] Failed to sync ribbon from auth state:", error);
  }
}

Office.onReady(() => {
  console.log('[Commands] Office.js ready in commands runtime');

  authService.onAuthChange((isAuthenticated) => {
    pptRibbonAuthManager.handleAuthStateChange(isAuthenticated, "auth change");
    if (isAuthenticated) {
      void cleanupOldConversations();
    }
  });

  void syncRibbonFromAuth();
});

// ============================================================================
// Dialog Error Codes (from Microsoft docs)
// https://learn.microsoft.com/en-us/office/dev/add-ins/develop/dialog-handle-errors-events
// ============================================================================

// Errors from displayDialogAsync
const DIALOG_ERROR = {
  DOMAIN_NOT_TRUSTED: 12004,      // Domain of URL not trusted (must be same as add-in)
  REQUIRES_HTTPS: 12005,          // URL uses HTTP instead of HTTPS
  DIALOG_ALREADY_OPEN: 12007,     // A dialog is already open from this host window
  USER_IGNORED_DIALOG: 12009,     // User chose to ignore (Office on web)
  POPUP_BLOCKED: 12011,           // Browser popup blocker blocked the dialog
};

// Errors/Events from DialogEventReceived
const DIALOG_EVENT = {
  PAGE_NOT_FOUND: 12002,          // No page exists at URL, or redirected to invalid URL
  REDIRECTED_TO_HTTP: 12003,      // Dialog was redirected to HTTP URL
  DIALOG_CLOSED: 12006,           // Dialog closed by user (X button) or COOP header issue
};

/**
 * Helper to get human-readable error message for displayDialogAsync errors
 */
function getDialogErrorMessage(errorCode: number): string {
  switch (errorCode) {
    case DIALOG_ERROR.DOMAIN_NOT_TRUSTED:
      return 'The dialog URL domain is not trusted. It must match the add-in domain.';
    case DIALOG_ERROR.REQUIRES_HTTPS:
      return 'The dialog URL must use HTTPS protocol.';
    case DIALOG_ERROR.DIALOG_ALREADY_OPEN:
      return 'A dialog is already open. Only one dialog can be open at a time.';
    case DIALOG_ERROR.USER_IGNORED_DIALOG:
      return 'User chose not to allow the dialog.';
    case DIALOG_ERROR.POPUP_BLOCKED:
      return 'Browser popup blocker prevented the dialog from opening.';
    default:
      return `Unknown dialog error: ${errorCode}`;
  }
}

/**
 * Helper to handle DialogEventReceived errors
 */
function handleDialogEvent(
  arg: { message: string; origin: string } | { error: number },
  dialogName: string,
  onClose: () => void
): void {
  if ('error' in arg) {
    switch (arg.error) {
      case DIALOG_EVENT.DIALOG_CLOSED:
        console.log(`[Commands] ${dialogName} closed by user (X button)`);
        onClose();
        break;
      case DIALOG_EVENT.PAGE_NOT_FOUND:
        console.error(`[Commands] ${dialogName} error: Page not found or redirected to invalid URL`);
        onClose();
        break;
      case DIALOG_EVENT.REDIRECTED_TO_HTTP:
        console.error(`[Commands] ${dialogName} error: Redirected to HTTP URL (HTTPS required)`);
        onClose();
        break;
      default:
        console.error(`[Commands] ${dialogName} unknown event error:`, arg.error);
        onClose();
        break;
    }
  }
}

// ============================================================================
// Dialog State Management
// Only one dialog can be open at a time from a host window
// ============================================================================

type DialogType =
  | 'login'
  | 'planning'
  | 'admin'
  | 'resync'
  | 'settings'
  | 'customInstructions'
  | 'promptLibrary'
  | 'about'
  | 'helpdesk'
  | 'citations'
  | null;
type ActiveDialogType = Exclude<DialogType, null>;
let currentOpenDialog: DialogType = null;
let currentDialogRef: Office.Dialog | null = null;

const dialogOpener = createDialogOpener<ActiveDialogType>({
  isAuthenticated: () => authService.isAuthenticated(),
  onRequireAuthentication: ({ event }) => {
    if (event) {
      openLoginDialog(event);
    }
  },
  loggerPrefix: "[Commands]",
  onStateChange: ({ currentOpenDialog: nextDialog, currentDialogRef: nextDialogRef }) => {
    currentOpenDialog = nextDialog;
    currentDialogRef = nextDialogRef;
  },
});

function completeCommandEvent(event?: Office.AddinCommands.Event): void {
  try {
    event?.completed();
  } catch (error) {
    console.warn('[Commands] Failed to complete add-in command event:', error);
  }
}

/**
 * Opens the Login dialog using Office.js Dialog API
 * Shared runtime ensures authService state is shared between dialog and taskpane
 */
function openLoginDialog(event?: Office.AddinCommands.Event) {
  console.log('[Commands] Opening login dialog... (current dialog:', currentOpenDialog, ')');

  // Notify taskpane that dialog is opening (start polling)
  window.dispatchEvent(new CustomEvent('carousel-login-dialog-opened'));

  let closeNotified = false;
  const notifyLoginClosed = (success: boolean): void => {
    if (closeNotified) {
      return;
    }
    closeNotified = true;
    console.log('[Commands] Completing login dialog event');
    window.dispatchEvent(
      new CustomEvent('carousel-login-dialog-closed', {
        detail: { success },
      })
    );
  };

  dialogOpener.openDialog({
    dialogType: 'login',
    dialogUrl: `${window.location.origin}/login.html`,
    event,
    options: {
      height: 60,
      width: 40,
      displayInIframe: false,
    },
    onOpenFailed: (error) => {
      const errorCode = error.code;
      console.error('[Commands] Error opening login dialog:', errorCode, getDialogErrorMessage(errorCode));
      if (errorCode === DIALOG_ERROR.DIALOG_ALREADY_OPEN) {
        console.warn('[Commands] Office reports dialog already open - resetting state');
      }
      notifyLoginClosed(false);
    },
    onOpened: () => {
      console.log('[Commands] Login dialog opened successfully');
    },
    onMessage: async ({ message, close }) => {
      console.log('[Commands] Message from login dialog:', message);
      try {
        const parsed = JSON.parse(message);

        if (parsed.status === 'success') {
          console.log('[Commands] Login successful!');
          console.log('[Commands] Received auth state:', parsed.authState);
          try {
            await authService.hydrateAuthState(parsed.authState);
            console.log('[Commands] Auth state hydrated in shared runtime');
            updateRibbonForAuthState(true);
            scheduleRibbonStateRetry();
            setTaskpaneStartupBehavior((Office as any)?.StartupBehavior?.load, "login dialog success");
            await cleanupOldConversations();
            notifyLoginClosed(true);
          } catch (error) {
            console.error('[Commands] Error hydrating auth state:', error);
            notifyLoginClosed(false);
          } finally {
            close();
          }
          return;
        }

        if (parsed.status === 'logout') {
          console.log('[Commands] Logout requested from login dialog');
          authService.logout();
          updateRibbonForAuthState(false);
          scheduleRibbonStateRetry();
          setTaskpaneStartupBehavior((Office as any)?.StartupBehavior?.none, "login dialog logout");
          return;
        }

        if (parsed.status === 'closed') {
          console.log('[Commands] Login dialog closed by user');
          notifyLoginClosed(false);
          close();
        }
      } catch (error) {
        console.error('[Commands] Error parsing login dialog message:', error);
        if (message === "login-success" || message === "dialog-closed") {
          notifyLoginClosed(message === "login-success");
          close();
        }
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Login dialog event:', args);
      handleDialogEvent(args, 'Login dialog', () => {
        notifyLoginClosed(false);
        finish();
      });
    },
  });
}

/**
 * Opens the Planning dialog using Office.js Dialog API
 */
function openPlanningDialog(event: Office.AddinCommands.Event) {
  console.log('[Commands] Opening planning dialog... (current dialog:', currentOpenDialog, ')');

  // Track plan state for cleanup on dialog close
  let currentPlanId: string | null = null;
  let hasPlanGenerated = false;

  // Helper to cleanup orphaned plan (with timeout to prevent hanging)
  const cleanupOrphanedPlan = async () => {
    const planIdToCleanup = currentPlanId;
    currentPlanId = null;
    if (planIdToCleanup && !hasPlanGenerated) {
      console.log('[Commands] Cleaning up orphaned plan shell:', planIdToCleanup);
      try {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Cleanup timeout')), 5000)
        );
        await Promise.race([
          backendClient.deletePlan(planIdToCleanup),
          timeoutPromise
        ]);
        console.log('[Commands] Orphaned plan deleted successfully');
      } catch (err) {
        console.error('[Commands] Failed to cleanup orphaned plan:', err);
      }
    }
  };

  dialogOpener.openGuardedDialog({
    dialogType: 'planning',
    dialogUrl: `${window.location.origin}/planning.html`,
    event,
    requiresAuth: true,
    options: {
      height: 70,
      width: 50,
      displayInIframe: false,
    },
    onOpenFailed: (error) => {
      const errorCode = error.code;
      console.error('[Commands] Error opening planning dialog:', errorCode, getDialogErrorMessage(errorCode));

      // Handle specific error: dialog already open
      if (errorCode === DIALOG_ERROR.DIALOG_ALREADY_OPEN) {
        console.warn('[Commands] Office reports dialog already open - resetting state');
      }
    },
    onOpened: () => {
      console.log('[Commands] Planning dialog opened successfully');
    },
    onMessage: async ({ message: rawMessage, dialog, close }) => {
      // Handle messages from the dialog
      // Set up handler immediately to catch early messages
      console.log('[Commands] Message from planning dialog received:', rawMessage);

      try {
        const message = JSON.parse(rawMessage);

        if (message.type === 'ready') {
          // Planning dialog is ready, send initialization data including auth
          console.log('[Commands] Planning dialog ready, sending init data...');

          // Initialize authService first to load tokens from localStorage
          // (Commands runtime needs to load tokens separately from taskpane)
          const isAuthenticated = await authService.initialize();
          console.log('[Commands] Auth initialized in commands runtime:', {
            isAuthenticated,
            hasToken: !!authService.getAccessToken()
          });

          // Get auth state from commands runtime
          const authState = authService.getAuthState();
          console.log('[Commands] Sending auth state to planning dialog:', {
            hasTokens: !!authState.tokens,
            hasUser: !!authState.user,
            userEmail: authState.user?.email
          });

          // Get current deck ID from taskpane context (where PowerPoint.run works)
          let currentdocumentId: string | null = null;
          try {
            currentdocumentId = await getOrCreatedocumentId();
            console.log('[Commands] Got current deck ID for dialog:', currentdocumentId);
          } catch (error) {
            console.warn('[Commands] Could not get current deck ID:', error);
          }

          // Send init message with auth, current deck ID, and other config
          try {
            dialog.messageChild(JSON.stringify({
              type: 'init',
              auth: authState,
              documentId: currentdocumentId, // The currently open presentation's deck ID
            }));
            console.log('[Commands] Sent init message to planning dialog with documentId:', currentdocumentId);
          } catch (error) {
            console.error('[Commands] Failed to send message to dialog:', error);
          }
        } else if (message.type === 'plan_created') {
          // Track that a plan shell was created (but not yet generated by LLM)
          currentPlanId = message.planId;
          console.log('[Commands] Plan shell created, tracking for cleanup:', currentPlanId);
        } else if (message.type === 'plan_generated') {
          // LLM successfully generated a plan - don't cleanup on close
          hasPlanGenerated = true;
          console.log('[Commands] Plan generated by LLM, will not cleanup on close');
        } else if (message.status === 'complete') {
          // Plan was completed successfully
          hasPlanGenerated = true;
          console.log('[Commands] Planning complete!');
          close();
        } else if (message.status === 'closed') {
          console.log('[Commands] Planning dialog closed by user');
          // Cleanup orphaned plan if user closes without generating
          cleanupOrphanedPlan().finally(close);
        }
      } catch (error) {
        console.error('[Commands] Error parsing planning dialog message:', error);
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Planning dialog event:', args);
      handleDialogEvent(args, 'Planning dialog', () => {
        // Cleanup orphaned plan if user closes without generating
        cleanupOrphanedPlan().finally(finish);
      });
    },
  });
}

/**
 * Get in-memory conversations from GlobalStateManager
 * Serializes them for transfer to the admin dialog
 */
function getInMemoryConversations(): InMemoryConversationData[] {
  const globalState = GlobalStateManager.getInstance();
  const conversations: InMemoryConversationData[] = [];
  
  // Get all conversation IDs from the registry
  const allConversationIds = Object.keys(globalState.conversationRegistry);
  
  for (const conversationId of allConversationIds) {
    try {
      const conversation = globalState.getConversation(conversationId);
      if (!conversation) continue;
      
      // Get threads for this conversation
      const threadManager = conversation.agentRegistry.threadManager;
      const conversationThreads = threadManager.getThreadsForConversation(conversationId);
      
      // Serialize threads
      const threads: InMemoryConversationData['threads'] = {};
      let messageCount = 0;
      let earliestCreatedAt = Date.now();
      let latestActivityAt = 0;
      
      for (const thread of conversationThreads) {
        threads[thread.threadId] = {
          threadId: thread.threadId,
          agentType: thread.agentType,
          conversationId: thread.conversationId,
          documentId: (thread as unknown as { documentId?: string }).documentId || thread.documentId,
          messages: thread.messages,
          createdAt: thread.createdAt,
          lastActivityAt: thread.lastActivityAt,
          tokenUsage: thread.tokenUsage,
        };
        messageCount += thread.messages.length;
        // Track earliest creation and latest activity from threads
        if (thread.createdAt < earliestCreatedAt) earliestCreatedAt = thread.createdAt;
        if (thread.lastActivityAt > latestActivityAt) latestActivityAt = thread.lastActivityAt;
      }
      
      // Context and image cache - leave empty for now
      // (full extraction would require the same logic as persistConversation)
      const contextCache: InMemoryConversationData['contextCache'] = {};
      const imageCache: InMemoryConversationData['imageCache'] = {};
      
      conversations.push({
        conversationId,
        origin: conversation.conversationOrigin,
        title: `In-Memory: ${conversation.conversationOrigin}`,
        documentId: conversation.presentationId,
        createdAt: new Date(earliestCreatedAt || Date.now()).toISOString(),
        lastActiveAt: new Date(latestActivityAt || Date.now()).toISOString(),
        messageCount,
        status: 'active',
        threads,
        contextCache,
        imageCache,
      });
    } catch (error) {
      console.warn(`[Commands] Error serializing conversation ${conversationId}:`, error);
    }
  }
  
  return conversations;
}

/**
 * Opens the Admin dialog using Office.js Dialog API
 * For clearing databases and viewing stats
 */
function openAdminDialog(event: Office.AddinCommands.Event) {
  console.log('[Commands] Opening admin dialog... (current dialog:', currentOpenDialog, ')');

  const dialogUrl = `${window.location.origin}/admin.html`;

  dialogOpener.openGuardedDialog({
    dialogType: 'admin',
    dialogUrl,
    event,
    requiresAuth: true,
    options: {
      height: 70,
      width: 45,
      displayInIframe: false,
    },
    onOpenFailed: (error) => {
      const errorCode = error.code;
      console.error('[Commands] Error opening admin dialog:', errorCode, getDialogErrorMessage(errorCode));

      // Handle specific error: dialog already open
      if (errorCode === DIALOG_ERROR.DIALOG_ALREADY_OPEN) {
        console.warn('[Commands] Office reports dialog already open - resetting state');
      }
    },
    onOpened: () => {
      console.log('[Commands] Admin dialog opened successfully, dialogRef:', !!currentDialogRef);
    },
    onMessage: async ({ message: rawMessage, close }) => {
      console.log('[Commands] 📨 Message from admin dialog received!');
      console.log('[Commands] Message content:', rawMessage?.substring(0, 200));

      try {
        // Try legacy format first (for close button)
        const legacyMessage = JSON.parse(rawMessage);
        if (legacyMessage.status === 'closed') {
          console.log('[Commands] Admin dialog closed by user');
          close();
          return;
        }
      } catch {
        // Ignore parse errors and try typed format next.
      }

      try {
        const message = parseAdminDialogMessage(rawMessage);
        if (!message) return;

        if (message.action === 'close') {
          console.log('[Commands] Admin dialog closed by user');
          close();
        } else if (message.action === 'get-in-memory-conversations') {
          console.log('[Commands] Admin dialog requested in-memory conversations');
          try {
            const conversations = getInMemoryConversations();
            console.log(`[Commands] Sending ${conversations.length} in-memory conversations to admin dialog`);
            if (currentDialogRef) {
              sendAdminToDialog(currentDialogRef, {
                action: 'in-memory-conversations',
                conversations,
              });
            }
          } catch (error) {
            console.error('[Commands] Error getting in-memory conversations:', error);
            if (currentDialogRef) {
              sendAdminToDialog(currentDialogRef, {
                action: 'error',
                message: `Failed to get in-memory conversations: ${error}`,
              });
            }
          }
        } else if (message.action === 'extract-tests-from-notes') {
          try {
            console.log('[Commands] Admin dialog requested test extraction from notes');
            if (!currentDialogRef) return;
            const result = await extractTestsFromSlideNotesInHost();
            if (result.success && result.tests) {
              sendAdminToDialog(currentDialogRef, {
                action: 'tests-from-notes-result',
                tests: result.tests,
              });
            } else {
              sendAdminToDialog(currentDialogRef, {
                action: 'error',
                message: result.error || 'Failed to extract tests from notes',
              });
            }
          } catch (error) {
            console.error('[Commands] Error extracting tests from notes:', error);
            if (currentDialogRef) {
              sendAdminToDialog(currentDialogRef, {
                action: 'error',
                message: `Failed to extract tests: ${error}`,
              });
            }
          }
        } else if (message.action === 'run-agent-test') {
          try {
            if (!currentDialogRef) return;
            const sendProgress = (stage: 'starting' | 'navigating' | 'capturing_before' | 'sending_prompt' | 'capturing_after' | 'done' | 'error', msg?: string) => {
              sendAdminToDialog(currentDialogRef!, {
                action: 'agent-test-progress',
                requestId: message.requestId,
                progress: { stage, message: msg },
              });
            };
            sendProgress('starting', 'Received request (host)');
            const result = await runAgentTestInHost(
              message.test,
              { runGroupId: (message as any).runGroupId, runIndex: (message as any).runIndex, runName: (message as any).runName },
              sendProgress
            );
            sendAdminToDialog(currentDialogRef, {
              action: 'agent-test-result',
              requestId: message.requestId,
              result,
            });
          } catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            if (currentDialogRef) {
              sendAdminToDialog(currentDialogRef, {
                action: 'agent-test-progress',
                requestId: message.requestId,
                progress: { stage: 'error', message: msg },
              });
              sendAdminToDialog(currentDialogRef, {
                action: 'agent-test-result',
                requestId: message.requestId,
                result: { success: false, error: msg },
              });
            }
          }
        } else if (message.action === 'replay-llm-call') {
          try {
            if (!currentDialogRef) return;
            sendAdminToDialog(currentDialogRef, {
              action: 'replay-llm-progress',
              requestId: message.requestId,
              progress: { stage: 'starting', message: 'Received replay request' },
            });
            const result = await replayLlmCallInHost({
              requestId: message.requestId,
              sessionId: message.sessionId,
              turnNumber: message.turnNumber,
              mode: message.mode,
              modelOverride: message.modelOverride,
              systemPromptOverride: message.systemPromptOverride,
              messagesOverride: message.messagesOverride as ChatMessage[] | undefined,
            }, (stage, msg) => {
              sendAdminToDialog(currentDialogRef!, {
                action: 'replay-llm-progress',
                requestId: message.requestId,
                progress: { stage, message: msg },
              });
            });
            sendAdminToDialog(currentDialogRef, {
              action: 'replay-llm-result',
              requestId: message.requestId,
              result,
            });
          } catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            if (currentDialogRef) {
              sendAdminToDialog(currentDialogRef, {
                action: 'replay-llm-result',
                requestId: message.requestId,
                result: { success: false, error: msg },
              });
            }
          }
        } else if (message.action === 'chat-send') {
          console.log('[Commands] 💬 chat-send action received:', {
            requestId: message.requestId,
            chatId: message.chatId,
            sessionId: message.sessionId,
            turnNumber: message.turnNumber,
            messageLength: message.message?.length,
            modelOverride: message.modelOverride,
          });
          try {
            if (!currentDialogRef) {
              console.error('[Commands] ❌ currentDialogRef is null in chat-send handler!');
              return;
            }
            console.log('[Commands] Sending chat-message-progress (starting) to dialog...');
            sendAdminToDialog(currentDialogRef, {
              action: 'chat-message-progress',
              requestId: message.requestId,
              chatId: message.chatId,
              progress: {
                stage: 'starting',
                message: 'Received chat message',
                details: {
                  requestId: message.requestId,
                  chatId: message.chatId,
                  sessionId: message.sessionId,
                  turnNumber: message.turnNumber,
                  messageLength: message.message.length,
                },
              },
            });
            console.log('[Commands] Calling chatSendInHost...');
            const result = await chatSendInHost({
              chatId: message.chatId,
              sessionId: message.sessionId,
              turnNumber: message.turnNumber,
              message: message.message,
              modelOverride: message.modelOverride,
              stream: message.stream,
            }, (stage, msg, details) => {
              sendAdminToDialog(currentDialogRef!, {
                action: 'chat-message-progress',
                requestId: message.requestId,
                chatId: message.chatId,
                progress: { stage, message: msg, details },
              });
            }, (chunk) => {
              sendAdminToDialog(currentDialogRef!, {
                action: 'chat-message-chunk',
                requestId: message.requestId,
                chatId: message.chatId,
                chunk,
              });
            });
            sendAdminToDialog(currentDialogRef, {
              action: 'chat-message-result',
              requestId: message.requestId,
              chatId: message.chatId,
              result,
            });
          } catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            if (currentDialogRef) {
              sendAdminToDialog(currentDialogRef, {
                action: 'chat-message-result',
                requestId: message.requestId,
                chatId: message.chatId,
                result: { success: false, error: msg },
              });
            }
          }
        } else if (message.action === 'chat-close') {
          console.log('[Commands] 🧹 chat-close action received:', { chatId: message.chatId });
          chatSessions.delete(message.chatId);
        } else if (message.action === 'toggle-dev-mode') {
          console.log('[Commands] Admin dialog toggled dev mode:', message.enabled);
          try {
            localStorage.setItem('carousel-dev-mode', message.enabled ? 'true' : 'false');
            console.log('[Commands] Dev mode saved to localStorage:', message.enabled);
          } catch (error) {
            console.warn('[Commands] Could not save dev mode to localStorage:', error);
          }
          window.dispatchEvent(new CustomEvent('carousel-dev-mode-changed', {
            detail: { enabled: message.enabled }
          }));
        } else if (message.action === 'get-experimental-settings') {
          console.log('[Commands] Admin dialog requested experimental settings');
          const globalState = GlobalStateManager.getInstance();
          const spacePlanningMode = globalState.getSpacePlanningMode();
          sendAdminToDialog(currentDialogRef, {
            action: 'experimental-settings',
            spacePlanningMode,
          });
        } else if (message.action === 'set-space-planning-mode') {
          console.log('[Commands] Admin dialog set space planning mode:', message.mode);
          const globalState = GlobalStateManager.getInstance();
          await globalState.setSpacePlanningMode(message.mode);
          console.log('[Commands] Space planning mode saved to settings:', message.mode);
          window.dispatchEvent(new CustomEvent('carousel-space-planning-mode-changed', {
            detail: { mode: message.mode }
          }));
        }
      } catch (error) {
        console.error('[Commands] Error parsing admin dialog message:', error);
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Admin dialog event:', args);
      handleDialogEvent(args, 'Admin dialog', finish);
    },
  });
}

/**
 * Opens a user-facing Manage Data dialog (database stats + clear actions)
 */
function openManageDataDialog(event: Office.AddinCommands.Event) {
  console.log('[Commands] Opening manage data dialog... (current dialog:', currentOpenDialog, ')');
  dialogOpener.openGuardedDialog({
    dialogType: 'admin',
    dialogUrl: `${window.location.origin}/admin.html?view=manage-data`,
    event,
    requiresAuth: true,
    options: {
      height: 70,
      width: 45,
      displayInIframe: false,
    },
    onOpened: () => {
      console.log('[Commands] Manage data dialog opened successfully, dialogRef:', !!currentDialogRef);
    },
    onMessage: ({ message, close }) => {
      try {
        const legacyMessage = JSON.parse(message);
        if (legacyMessage.status === 'closed') {
          close();
          return;
        }
      } catch {
        // Ignore parse errors and try typed format next.
      }

      const parsed = parseAdminDialogMessage(message);
      if (!parsed) return;

      if (parsed.action === 'close') {
        close();
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Manage data dialog event:', args);
      handleDialogEvent(args, 'Manage data dialog', finish);
    },
  });
}

/**
 * Opens the Settings dialog using Office.js Dialog API
 * Provides full-width interface for configuring Carousel settings
 * @param tabId - Optional tab ID to open ('general', 'carousel-chat', 'excel-export', 'indexing')
 */
function isMeaningfulDeckTitle(title?: string | null): boolean {
  if (!title) return false;
  const normalized = title.trim();
  if (!normalized) return false;
  const lower = normalized.toLowerCase();
  if (lower === "untitled") return false;
  if (lower.includes("property in win32")) return false;
  return true;
}

function getDeckNameFromDocumentUrl(documentUrl?: string): string | null {
  if (!documentUrl) {
    return null;
  }

  try {
    const pathParts = documentUrl.split(/[/\\]/);
    const fullFilename = decodeURIComponent(pathParts[pathParts.length - 1] || "");
    const noQuery = fullFilename.split("?")[0];
    const name = noQuery.replace(/\.pptx?$/i, "").trim();
    return isMeaningfulDeckTitle(name) ? name : null;
  } catch {
    return null;
  }
}

async function buildSettingsDialogUrlForCommands(tabId?: string): Promise<string> {
  console.log("[Commands] Building settings dialog URL", { tabId });
  const params = new URLSearchParams();
  if (tabId) {
    params.set("tab", tabId);
  }

  let resolveddocumentId: string | null = null;
  let resolvedDeckName: string = "Current Presentation";
  let resolvedDeckOverrideEnabled: boolean | null = null;
  let deckNameSource: "default" | "documentUrl" | "presentationTitle" | "catalog" = "default";

  try {
    resolveddocumentId = await getOrCreatedocumentId();
    if (resolveddocumentId) {
      params.set("documentId", resolveddocumentId);
    }
  } catch (error) {
    console.warn("[Commands] Could not resolve documentId while building settings dialog URL:", error);
  }

  if (resolveddocumentId) {
    const documentUrl = Office.context?.document?.url;
    const deckNameFromUrl = getDeckNameFromDocumentUrl(documentUrl);
    if (deckNameFromUrl) {
      resolvedDeckName = deckNameFromUrl;
      deckNameSource = "documentUrl";
      console.log("[Commands] Using deck name from document URL", {
        documentId: resolveddocumentId,
        deckName: resolvedDeckName,
        documentUrl,
      });
    }

    try {
      await PowerPoint.run(async (context) => {
        const presentation = context.presentation;
        presentation.load("title");
        await context.sync();
        const title = (presentation.title || "").trim();
        if (isMeaningfulDeckTitle(title) && deckNameSource !== "documentUrl") {
          resolvedDeckName = title;
          deckNameSource = "presentationTitle";
        }
      });
    } catch (error) {
      console.warn("[Commands] Could not read presentation title while building settings dialog URL:", error);
    }

    try {
      const catalog = await backendClient.getPresentationsCatalog();
      const matchingDeck = catalog.find((entry) => entry.deck_id === resolveddocumentId);
      const catalogTitle = matchingDeck?.title?.trim();
      if (isMeaningfulDeckTitle(catalogTitle)) {
        resolvedDeckName = catalogTitle!;
        deckNameSource = "catalog";
      }
    } catch (error) {
      console.warn("[Commands] Could not read deck name from catalog while building settings dialog URL:", error);
    }

    params.set("deckName", resolvedDeckName);

    try {
      const storage = new LiveTrackingStorage();
      await storage.initialize();
      const deckState = await storage.getDeckState(resolveddocumentId);
      if (typeof deckState?.indexingEnabled === "boolean") {
        resolvedDeckOverrideEnabled = deckState.indexingEnabled;
        params.set("deckOverrideEnabled", deckState.indexingEnabled ? "1" : "0");
      }
    } catch (error) {
      console.warn("[Commands] Could not read deck override while building settings dialog URL:", error);
    }
  }

  const query = params.toString();
  const url = `${window.location.origin}/settings.html${query ? `?${query}` : ""}`;
  console.log("[Commands] Settings dialog URL prepared", {
    url,
    documentId: resolveddocumentId,
    deckName: resolvedDeckName,
    deckNameSource,
    deckOverrideEnabled: resolvedDeckOverrideEnabled,
  });
  return url;
}

function openSettingsModal(event: Office.AddinCommands.Event, tabId?: string) {
  console.log('[Commands] Opening settings dialog...', tabId ? `tab: ${tabId}` : '');
  
  // Check authentication first
  if (!authService.isAuthenticated()) {
    console.log('[Commands] User not authenticated, cannot open settings dialog');
    openLoginDialog(event);
    return;
  }

  void (async () => {
    let dialogUrl = `${window.location.origin}/settings.html`;
    try {
      dialogUrl = await buildSettingsDialogUrlForCommands(tabId);
    } catch (error) {
      console.warn("[Commands] Falling back to default settings URL due to URL-build error:", error);
    }

    dialogOpener.openGuardedDialog({
      dialogType: 'settings',
      dialogUrl,
      event,
      requiresAuth: true,
      options: {
        height: 55, // 55% of screen height
        width: 40,  // 40% of screen width
        displayInIframe: false,
      },
      onOpenFailed: (error) => {
        console.error('[Commands] Error opening settings dialog:', error);
      },
      onOpened: () => {
        console.log('[Commands] Settings dialog opened successfully');
      },
      onMessage: ({ message: rawMessage, close }) => {
        console.log('[Commands] Message from settings dialog:', rawMessage);

        try {
          const message = JSON.parse(rawMessage);

          if (message.status === 'logout' && message.action === 'disableButtons') {
            console.log('[Commands] User logged out, disabling ribbon buttons...');
            authService.logout();

            // Close the taskpane if it's open
            try {
              Office.addin.hide();
              console.log('[Commands] Taskpane hidden after logout');
            } catch (error) {
              console.error('[Commands] Error hiding taskpane:', error);
            }

            updateRibbonForAuthState(false);
            scheduleRibbonStateRetry();
            setTaskpaneStartupBehavior((Office as any)?.StartupBehavior?.none, "settings dialog logout");
            close();
          } else if (message.status === 'closed') {
            console.log('[Commands] Settings dialog closed by user');
            close();
          }
        } catch (error) {
          console.error('[Commands] Error parsing dialog message:', error);
        }
      },
      onDialogEvent: ({ args, finish }) => {
        console.log('[Commands] Settings dialog event:', args);
        if ('error' in args && args.error === 12006) {
          // Dialog closed by user clicking X
          console.log('[Commands] Settings dialog closed by user (X button)');
          finish();
        }
      },
    });
  })();
}

/**
 * Opens the Re-Sync dialog using Office.js Dialog API
 * Provides full-width interface for syncing Excel artifacts
 * 
 * Uses a message bridge pattern:
 * - Dialog sends requests (scan, update) via messageParent()
 * - Parent (this code) performs PowerPoint.run() operations
 * - Parent sends results back via dialog.messageChild()
 */
async function openResyncModal(event?: Office.AddinCommands.Event) {
  console.log('[Commands] Opening resync dialog... (current dialog:', currentOpenDialog, ')');

  try {
    await authService.initialize();
  } catch (error) {
    console.error('[Commands] Failed to initialize auth for resync dialog:', error);
  }

  if (!authService.isAuthenticated()) {
    console.log('[Commands] User not authenticated, opening login before resync dialog');
    openLoginDialog(event);
    return;
  }

  const dialogUrl = `${window.location.origin}/resync.html`;
  const resyncService = ResyncService.getInstance();
  dialogOpener.openGuardedDialog({
    dialogType: 'resync',
    dialogUrl,
    event,
    requiresAuth: true,
    options: {
      height: 80,
      width: 60,
      displayInIframe: false,
    },
    onOpenFailed: (error) => {
      const errorCode = error.code;
      console.error('[Commands] Error opening resync dialog:', errorCode, getDialogErrorMessage(errorCode));
    },
    onOpened: () => {
      console.log('[Commands] Resync dialog opened successfully');
    },
    onMessage: async ({ message: rawMessage, dialog, close }) => {
      console.log('[Commands] Message from resync dialog:', rawMessage);

      const message = parseDialogMessage(rawMessage);
      if (!message) return;

      switch (message.action) {
        case 'ready':
          console.log('[Commands] Resync dialog ready');
          sendToDialog(dialog, { action: 'init' });
          break;

        case 'scan':
          console.log('[Commands] Starting presentation scan...');
          try {
            const scanResult = await resyncService.scanPresentation((progress) => {
              sendToDialog(dialog, { action: 'scanProgress', progress });
            });

            console.log(`[Commands] Scan found ${scanResult.artifacts.length} artifacts`);

            if (scanResult.artifacts.length === 0) {
              sendToDialog(dialog, {
                action: 'scanComplete',
                artifacts: [],
                missingFiles: [],
                summary: { upToDate: 0, outdated: 0, missingFile: 0, error: 0, total: 0 },
              });
              break;
            }

            const { artifacts: checkedArtifacts, missingFiles } =
              await resyncService.checkAllArtifactsStatus(
                scanResult.artifacts,
                (progress) => sendToDialog(dialog, { action: 'scanProgress', progress })
              );

            const summary = calculateSyncSummary(checkedArtifacts);
            console.log('[Commands] Status check complete:', summary);

            sendToDialog(dialog, {
              action: 'scanComplete',
              artifacts: checkedArtifacts,
              missingFiles,
              summary,
            });
          } catch (error) {
            console.error('[Commands] Scan error:', error);
            sendToDialog(dialog, {
              action: 'error',
              message: error instanceof Error ? error.message : String(error),
            });
          }
          break;

        case 'rescanOne':
          console.log(`[Commands] Rescanning single artifact ${message.artifact.artifactId}...`);
          try {
            const rescanned = await resyncService.rescanOneArtifact(message.artifact, true);
            sendToDialog(dialog, { action: 'rescanOneComplete', artifact: rescanned });
          } catch (error) {
            console.error('[Commands] Single rescan error:', error);
            sendToDialog(dialog, {
              action: 'error',
              message: error instanceof Error ? error.message : String(error),
            });
          }
          break;

        case 'update':
          console.log(`[Commands] Updating ${message.artifacts.length} artifacts...`);
          console.log('[Commands] Artifacts to update:', message.artifacts.map(a => a.artifactId));
          try {
            console.log('[Commands] Calling resyncService.updateArtifacts()...');
            const updateStartTime = Date.now();

            const updateResult = await resyncService.updateArtifacts(
              message.artifacts,
              (current, total) => {
                console.log(`[Commands] Update progress: ${current}/${total}`);
                sendToDialog(dialog, { action: 'updateProgress', current, total });
              }
            );

            const updateDuration = Date.now() - updateStartTime;
            console.log(`[Commands] updateArtifacts() returned after ${updateDuration}ms`);
            console.log('[Commands] Update result:', updateResult);
            console.log(`[Commands] Update complete: ${updateResult.succeeded}/${updateResult.total} succeeded`);

            console.log('[Commands] Sending updateComplete to dialog...');
            sendToDialog(dialog, { action: 'updateComplete', result: updateResult });
            console.log('[Commands] updateComplete sent to dialog');
          } catch (error) {
            console.error('[Commands] Update error:', error);
            console.error('[Commands] Error stack:', error instanceof Error ? error.stack : 'no stack');
            sendToDialog(dialog, {
              action: 'error',
              message: error instanceof Error ? error.message : String(error),
            });
          }
          break;

        case 'relocateFile':
          console.log(`[Commands] Relocating file ${message.excelId} to ${message.newPath}`);
          try {
            await resyncService.relocateSourceFile(message.excelId, message.newPath);
          } catch (error) {
            console.error('[Commands] Relocate error:', error);
            sendToDialog(dialog, {
              action: 'error',
              message: error instanceof Error ? error.message : String(error),
            });
          }
          break;

        case 'close':
          console.log('[Commands] Resync dialog closed by user');
          close();
          break;
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Resync dialog event:', args);
      handleDialogEvent(args, 'Resync dialog', finish);
    },
  });
}

/**
 * Opens the Custom Instructions dialog using Office.js Dialog API
 * For managing custom instructions for Carousel Chat
 */
function openCustomInstructionsModal(event: Office.AddinCommands.Event) {
  console.log('[Commands] Opening custom instructions dialog...');

  dialogOpener.openGuardedDialog({
    dialogType: 'customInstructions',
    dialogUrl: `${window.location.origin}/customInstructions.html`,
    event,
    requiresAuth: true,
    options: {
      height: 55,
      width: 40,
      displayInIframe: false,
    },
    onOpened: () => {
      console.log('[Commands] Custom Instructions dialog opened successfully');
    },
    onMessage: ({ message, close }) => {
      console.log('[Commands] Message from custom instructions dialog:', message);
      try {
        const parsed = JSON.parse(message);
        if (parsed.status === 'closed') {
          console.log('[Commands] Custom Instructions dialog closed by user');
          close();
        }
      } catch (error) {
        console.error('[Commands] Error parsing dialog message:', error);
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Custom Instructions dialog event:', args);
      if ('error' in args && args.error === 12006) {
        console.log('[Commands] Custom Instructions dialog closed by user (X button)');
        finish();
      }
    },
  });
}

/**
 * Opens the Prompt Library dialog using Office.js Dialog API
 * For managing saved prompts
 */
function openPromptLibraryModal(event: Office.AddinCommands.Event) {
  console.log('[Commands] Opening prompt library dialog...');

  dialogOpener.openGuardedDialog({
    dialogType: 'promptLibrary',
    dialogUrl: `${window.location.origin}/promptLibrary.html`,
    event,
    requiresAuth: true,
    options: {
      height: 55,
      width: 40,
      displayInIframe: false,
    },
    onOpened: () => {
      console.log('[Commands] Prompt Library dialog opened successfully');
    },
    onMessage: ({ message, close }) => {
      console.log('[Commands] Message from prompt library dialog:', message);
      try {
        const parsed = JSON.parse(message);
        if (parsed.status === 'closed') {
          console.log('[Commands] Prompt Library dialog closed by user');
          close();
        }
      } catch (error) {
        console.error('[Commands] Error parsing dialog message:', error);
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Prompt Library dialog event:', args);
      if ('error' in args && args.error === 12006) {
        console.log('[Commands] Prompt Library dialog closed by user (X button)');
        finish();
      }
    },
  });
}

/**
 * Opens the About dialog using Office.js Dialog API
 * Displays version information and support resources
 */
function openAboutModal(event: Office.AddinCommands.Event) {
  console.log('[Commands] Opening about dialog...');

  dialogOpener.openDialog({
    dialogType: 'about',
    dialogUrl: `${window.location.origin}/about.html`,
    event,
    options: {
      height: 30,
      width: 24,
      displayInIframe: false,
    },
    onOpened: () => {
      console.log('[Commands] About dialog opened successfully');
    },
    onMessage: ({ message, close }) => {
      console.log('[Commands] Message from about dialog:', message);
      try {
        const parsed = JSON.parse(message);
        if (parsed.status === 'closed') {
          console.log('[Commands] About dialog closed by user');
          close();
        }
      } catch (error) {
        console.error('[Commands] Error parsing dialog message:', error);
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] About dialog event:', args);
      if ('error' in args && args.error === 12006) {
        console.log('[Commands] About dialog closed by user (X button)');
        finish();
      }
    },
  });
}

/**
 * Opens the Helpdesk dialog using Office.js Dialog API
 * Displays an iframe to carousel.crisp.help
 */
function openHelpdeskModal(event: Office.AddinCommands.Event) {
  console.log('[Commands] Opening helpdesk dialog...');

  dialogOpener.openDialog({
    dialogType: 'helpdesk',
    dialogUrl: `${window.location.origin}/helpdesk.html`,
    event,
    options: {
      height: 80,
      width: 70,
      displayInIframe: false,
    },
    onOpened: () => {
      console.log('[Commands] Helpdesk dialog opened successfully');
    },
    onMessage: ({ message, close }) => {
      console.log('[Commands] Message from helpdesk dialog:', message);
      try {
        const parsed = JSON.parse(message);
        if (parsed.status === 'closed') {
          console.log('[Commands] Helpdesk dialog closed by user');
          close();
        }
      } catch (error) {
        console.error('[Commands] Error parsing dialog message:', error);
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log('[Commands] Helpdesk dialog event:', args);
      if ('error' in args && args.error === 12006) {
        console.log('[Commands] Helpdesk dialog closed by user (X button)');
        finish();
      }
    },
  });
}

/**
 * Opens the citations dialog from the PowerPoint text context menu.
 * Uses shape metadata + mapsTo matching to scope results to selected text.
 */
async function openCitationsDialog(event: Office.AddinCommands.Event) {
  console.log("[Commands] Opening citations dialog from context menu...");

  const dialogUrl = `${window.location.origin}/citations.html`;
  let selectedText = "";
  let shapeContext: CitationDialogShapeContext | undefined;
  let citations: CitationDialogEntry[] = [];
  let totalCitationsOnShape = 0;
  let matchedCitationCount = 0;

  try {
    selectedText = await getSelectedTextFromDocument();
    const selectedShapeContext = await getSelectedShapeContext();

    if (selectedShapeContext) {
      shapeContext = {
        slideIndex: selectedShapeContext.slideIndex + 1,
        shapeId: selectedShapeContext.shapeId,
        shapeType: selectedShapeContext.shapeType,
        shapeName: selectedShapeContext.shapeName,
      };

      const metadata = await getShapeMetadata(
        selectedShapeContext.slideIndex,
        selectedShapeContext.shapeId
      );

      const allEntries = metadata?.entries ?? [];
      const citationEntries = allEntries.filter(
        (entry) => entry?.type?.toLowerCase() === "citation"
      ) as MetadataEntry[];
      totalCitationsOnShape = citationEntries.length;

      const filteredEntries = filterCitationEntriesBySelection(citationEntries, selectedText);
      matchedCitationCount = filteredEntries.length;
      citations = filteredEntries.map((entry) => ({
        sourceContextId: entry.sourceContextId,
        type: entry.type,
        mapsTo: entry.mapsTo,
        payload: entry.payload,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt,
      }));
    }
  } catch (error) {
    console.error("[Commands] Failed to build citations payload:", error);
  }

  let completed = false;
  const completeEvent = () => {
    if (completed) return;
    completed = true;
    event.completed();
  };

  dialogOpener.openDialog({
    dialogType: 'citations',
    dialogUrl,
    event,
    options: {
      height: 75,
      width: 55,
      displayInIframe: false,
    },
    onOpenFailed: (error) => {
      console.error("[Commands] Error opening citations dialog:", error);
      completeEvent();
    },
    onOpened: () => {
      console.log("[Commands] Citations dialog opened successfully");
    },
    onMessage: ({ message: rawMessage, dialog, close }) => {
      const message = parseCitationsDialogMessage(rawMessage);
      if (!message) {
        return;
      }

      if (message.action === "ready") {
        sendCitationsToDialog(dialog, {
          action: "init",
          selectedText,
          shapeContext,
          citations,
          totalCitationsOnShape,
          matchedCitationCount,
        });
        return;
      }

      if (message.action === "closed") {
        close();
      }
    },
    onDialogEvent: ({ args, finish }) => {
      console.log("[Commands] Citations dialog event:", args);
      if ("error" in args && args.error === 12006) {
        finish();
      }
    },
  });
}

// Register the functions with Office.js
(global as any).openLoginDialog = openLoginDialog;
(global as any).openPlanningDialog = openPlanningDialog;
(global as any).openAdminDialog = openAdminDialog;
(global as any).openManageDataDialog = openManageDataDialog;
(global as any).openSettingsModal = openSettingsModal;
(global as any).openResyncModal = openResyncModal;
(global as any).openCustomInstructionsModal = openCustomInstructionsModal;
(global as any).openPromptLibraryModal = openPromptLibraryModal;
(global as any).openAboutModal = openAboutModal;
(global as any).openHelpdeskModal = openHelpdeskModal;
(global as any).openCitationsDialog = openCitationsDialog;

if (typeof window !== "undefined") {
  window.addEventListener("carousel-open-resync", () => {
    console.log('[Commands] Received carousel-open-resync event from taskpane');
    void openResyncModal(undefined);
  });
}

Office.onReady(() => {
  const actions = (Office as unknown as { actions?: { associate?: (name: string, fn: (...args: unknown[]) => unknown) => void } }).actions;
  if (!actions || typeof actions.associate !== "function") {
    console.warn('[Commands] Office.actions.associate unavailable; relying on global command exports.');
    return;
  }

  actions.associate("openLoginDialog", openLoginDialog);
  actions.associate("openPlanningDialog", openPlanningDialog);
  actions.associate("openAdminDialog", openAdminDialog);
  actions.associate("openManageDataDialog", openManageDataDialog);
  actions.associate("openSettingsModal", openSettingsModal);
  actions.associate("openResyncModal", openResyncModal);
  actions.associate("openCustomInstructionsModal", openCustomInstructionsModal);
  actions.associate("openPromptLibraryModal", openPromptLibraryModal);
  actions.associate("openAboutModal", openAboutModal);
  actions.associate("openHelpdeskModal", openHelpdeskModal);
  actions.associate("openCitationsDialog", openCitationsDialog);

  console.log("[Commands] Office command actions associated successfully.");
});